import React, { useState, useEffect, useMemo } from 'react';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  ColumnDef
} from '@tanstack/react-table';
import { TestCase, TestCaseStatus, parseTestCaseDetails, formatTimeTaken } from '../TestExecution/const/TestSummary';
import testExecutionService, {
  TestCaseResponse,
  TestCaseSortBy,
  TestCaseSortOrder,
  GetTestCasesParams
} from '../../../api/testExecutionService';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Pagination, Button, Tag, Pill, Select, MenuItem, Loading } from '@dsi/react-eds';
import { useTheme } from '../../../hooks/useTheme';
import styles from './TestCaseTable.module.css';
import TestCaseLogDialog from './TestCaseLogDialog';
import { useFetchLogs } from '../../Pipeline/log-viewer/useFetchLogs';

interface TestCaseTableProps {
  testExecutionId: string;
}

// Map frontend column IDs to API sortBy values
const columnToSortByMap: Record<string, TestCaseSortBy> = {
  test_case_name: 'testCaseName',
  time_taken: 'timeTaken',
  result: 'result',
  test_case_id: 'testCaseId',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt'
};

const TestCaseTable: React.FC<TestCaseTableProps> = ({ testExecutionId }) => {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();

  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPillFilters, setSelectedPillFilters] = useState<TestCaseStatus[]>([]);
  const [debouncedFilters, setDebouncedFilters] = useState<ColumnFiltersState>([]);
  const [filterTimeout, setFilterTimeout] = useState<NodeJS.Timeout | null>(null);

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedTestCase, setSelectedTestCase] = useState<TestCase>();
  const { logs, isLoading, error } = useFetchLogs(selectedTestCase?.log_url || '');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20 // Default aligned to API default
  });

  // Convert ColumnFiltersState to API parameters format
  const convertFiltersToApiParams = (filters: ColumnFiltersState): Partial<GetTestCasesParams> => {
    const apiParams: Partial<GetTestCasesParams> = {};

    filters.forEach((filter) => {
      if (filter.id === 'result' && Array.isArray(filter.value)) {
        // result filter: convert array to comma-separated string
        apiParams.result = filter.value.join(',');
      } else if (filter.id === 'test_case_name' && typeof filter.value === 'string' && filter.value.trim() !== '') {
        // test_case_name filter: only add if not empty
        apiParams.test_case_name = filter.value;
      } else if (filter.id === 'details' && typeof filter.value === 'string' && filter.value.trim() !== '') {
        // details filter: only add if not empty
        apiParams.details = filter.value;
      } else if (filter.id === 'time_taken' && typeof filter.value === 'string' && filter.value.trim() !== '') {
        // time_taken filter: only add if not empty
        apiParams.time_taken = filter.value;
      }
    });

    return apiParams;
  };

  // Fetch test cases from API
  useEffect(() => {
    const fetchTestCases = async () => {
      if (!testExecutionId) {
        setTestCases([]);
        setTotal(0);
        return;
      }

      setLoading(true);

      try {
        // Map sorting state to API parameters
        const sortBy: TestCaseSortBy = sorting.length > 0 && sorting[0].id ? columnToSortByMap[sorting[0].id] || 'createdAt' : 'createdAt';
        const sortOrder: TestCaseSortOrder = sorting.length > 0 && sorting[0].desc ? 'DESC' : 'ASC';

        // Convert filters to API parameters format
        const filterParams = convertFiltersToApiParams(debouncedFilters);

        const response = await testExecutionService.getTestCases(testExecutionId, {
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          sortBy,
          sortOrder,
          ...filterParams // Spread filter parameters
        });

        // Transform API response to TestCase format
        const transformedCases: TestCase[] = response.data.map((item: TestCaseResponse) => {
          const detailsArray = parseTestCaseDetails(item.details);
          // Store the original details array for display
          return {
            test_case_id: item.test_case_id,
            test_case_name: item.test_case_name,
            details: detailsArray.length > 0 ? JSON.stringify(detailsArray) : '[]', // Store as JSON string for display
            time_taken: formatTimeTaken(item.time_taken),
            result: item.result as TestCaseStatus,
            log_url: item.log_url
          };
        });

        setTestCases(transformedCases);
        setTotal(response.total);
      } catch (error: unknown) {
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching test cases',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        setTestCases([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchTestCases();

    // Cleanup function: clear debounce timeout
    return () => {
      if (filterTimeout) {
        clearTimeout(filterTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testExecutionId, pagination.pageIndex, pagination.pageSize, sorting, debouncedFilters, addNotifications]);

  const testCaseColumns = useMemo<ColumnDef<TestCase>[]>(
    () => [
      {
        accessorKey: 'test_case_name',
        header: 'Test Case Name',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'details',
        header: 'Details',
        cell: (info) => {
          const value = info.getValue() as string;
          let detailsArray: string[] = [];
          try {
            detailsArray = JSON.parse(value);
          } catch {
            // If parsing fails, treat as empty
            detailsArray = [];
          }

          if (detailsArray.length === 0) {
            return <span>No messages to show</span>;
          }

          return (
            <div>
              {detailsArray.map((msg, index) => (
                <div key={index} style={{ marginBottom: index < detailsArray.length - 1 ? '4px' : '0' }}>
                  {msg}
                </div>
              ))}
            </div>
          );
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'time_taken',
        header: 'Time Taken',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'result',
        header: 'Result',
        cell: (info) => {
          const status = info.getValue() as TestCaseStatus;
          switch (status) {
            case TestCaseStatus.SUCCESS:
              return (
                <ReactEDSWrapper component={Tag} big color="green" icon="success">
                  {status}
                </ReactEDSWrapper>
              );
            case TestCaseStatus.FAILURE:
              return (
                <ReactEDSWrapper component={Tag} big color="red" icon="failed">
                  {status}
                </ReactEDSWrapper>
              );
            case TestCaseStatus.SKIPPED:
              return (
                <ReactEDSWrapper component={Tag} big color="yellow" icon="info">
                  {status}
                </ReactEDSWrapper>
              );
            case TestCaseStatus.WARNING:
              return (
                <ReactEDSWrapper component={Tag} big color="orange" icon="triangle-warning">
                  {status}
                </ReactEDSWrapper>
              );
            case TestCaseStatus.RUNNING:
              return (
                <ReactEDSWrapper component={Tag} big color="blue" icon="time">
                  {status}
                </ReactEDSWrapper>
              );
            default:
              return <ReactEDSWrapper component={Tag}>{status}</ReactEDSWrapper>;
          }
        },
        footer: (props) => props.column.id,
        filterFn: (row, columnId, filterValue: TestCaseStatus[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          return filterValue.includes(row.getValue(columnId) as TestCaseStatus);
        }
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const logUrl = row.original.log_url;
          const hasLogUrl = logUrl && logUrl.trim() !== '';

          return (
            <div className={styles.actionButtons}>
              <ReactEDSWrapper
                component={Button}
                icon="terminal"
                aria-label={`Open log for ${row.original.test_case_name}`}
                title={hasLogUrl ? 'Open log' : 'Log not available'}
                disabled={!hasLogUrl}
                onClick={() => {
                  if (hasLogUrl) {
                    if (!logUrl.includes('jcat')) {
                      setSelectedTestCase(row.original);
                      setShowDialog(true);
                    } else {
                      window.open(logUrl, '_blank');
                    }
                  }
                }}
              />
              {/* <ReactEDSWrapper
                component={Button}
                icon="download-save"
                disabled={true}
                aria-label={`Download log for ${row.original.test_case_name}`}
                title="Download Log"
                onClick={() => {
                  // TODO: Implement download log
                  console.log('Download log for:', row.original.test_case_id);
                }}
              /> */}
            </div>
          );
        },
        enableSorting: false
      }
    ],
    []
  );

  const getColumnMinWidth = (columnId: string) => {
    const widths = {
      test_case_name: '200px',
      details: '250px',
      time_taken: '150px',
      result: '120px',
      actions: '120px'
    };
    return widths[columnId as keyof typeof widths] || '120px';
  };

  const table = useReactTable({
    data: testCases,
    columns: testCaseColumns,
    state: {
      pagination,
      sorting,
      columnFilters
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      if (filterTimeout) clearTimeout(filterTimeout);
      const timeout = setTimeout(() => {
        setDebouncedFilters(typeof updater === 'function' ? updater(columnFilters) : updater);
      }, 500);
      setFilterTimeout(timeout);
    },
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualPagination: true, // Server-side pagination
    manualSorting: true, // Server-side sorting
    manualFiltering: true, // Server-side filtering
    pageCount: Math.ceil(total / pagination.pageSize),
    getCoreRowModel: getCoreRowModel()
  });

  // Handle pill click - toggle filter and sync with column filter
  const handlePillClick = (status: TestCaseStatus) => {
    const isSelected = selectedPillFilters.includes(status);
    let newPillFilters: TestCaseStatus[];

    if (isSelected) {
      newPillFilters = selectedPillFilters.filter((s) => s !== status);
    } else {
      newPillFilters = [...selectedPillFilters, status];
    }

    setSelectedPillFilters(newPillFilters);

    // Update result column filter using table's setColumnFilters to trigger debounce
    table.setColumnFilters([
      {
        id: 'result',
        value: newPillFilters.length > 0 ? newPillFilters : undefined
      }
    ]);

    // Auto-open filters when a pill is selected
    if (!isSelected && !showFilters) {
      setShowFilters(true);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setColumnFilters([]);
    setDebouncedFilters([]);
    setSelectedPillFilters([]);
    if (filterTimeout) {
      clearTimeout(filterTimeout);
      setFilterTimeout(null);
    }
    table.setPageIndex(0);
  };

  // Sync pill filters when result column filter changes
  useEffect(() => {
    const resultFilter = table.getColumn('result')?.getFilterValue() as TestCaseStatus[] | undefined;
    if (resultFilter && Array.isArray(resultFilter)) {
      setSelectedPillFilters(resultFilter);
    } else if (!resultFilter) {
      setSelectedPillFilters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedTestCase(undefined);
  };

  return (
    <>
      <div className={styles.tableTitle}>Test Case Info</div>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.itemCount}>
            {loading ? (
              'Loading...'
            ) : total > 0 ? (
              <>
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, total)} of {total} items
              </>
            ) : (
              'No items'
            )}
          </span>
          {/* Pill Filters */}
          <div className={styles.pillGroup}>
            <ReactEDSWrapper
              component={Pill}
              className={
                selectedPillFilters.includes(TestCaseStatus.SUCCESS)
                  ? 'pill toggle selected'
                  : selectedPillFilters.length > 0
                    ? 'pill toggle unselected'
                    : 'pill toggle'
              }
              icon="success"
              onClick={() => handlePillClick(TestCaseStatus.SUCCESS)}
            >
              Successful
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={Pill}
              className={
                selectedPillFilters.includes(TestCaseStatus.FAILURE)
                  ? 'pill toggle selected'
                  : selectedPillFilters.length > 0
                    ? 'pill toggle unselected'
                    : 'pill toggle'
              }
              icon="failed"
              onClick={() => handlePillClick(TestCaseStatus.FAILURE)}
            >
              Failed
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={Pill}
              className={
                selectedPillFilters.includes(TestCaseStatus.SKIPPED)
                  ? 'pill toggle selected'
                  : selectedPillFilters.length > 0
                    ? 'pill toggle unselected'
                    : 'pill toggle'
              }
              icon="info"
              onClick={() => handlePillClick(TestCaseStatus.SKIPPED)}
            >
              Skipped
            </ReactEDSWrapper>
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Close Filters' : 'Filters'}
          </ReactEDSWrapper>
          {showFilters && (
            <ReactEDSWrapper component={Button} icon="filter-clear" onClick={handleClearFilters}>
              Clear Filters
            </ReactEDSWrapper>
          )}
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={styles.headerCell}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    minWidth: getColumnMinWidth(header.column.id)
                  }}
                >
                  <div
                    className={header.column.getCanSort() ? styles.sortableHeader : styles.unsortableHeader}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span className={styles.sortIcon}>
                        {header.column.getIsSorted() === 'asc' ? (
                          <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }}></i>
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <i className="icon icon-sort-down"></i>
                        ) : (
                          <i className="icon icon-sorting"></i>
                        )}
                      </span>
                    )}
                  </div>
                  {header.column.getCanFilter() && showFilters ? (
                    <div className={styles.filterContainer}>
                      {header.column.id === 'result' ? (
                        <ReactEDSWrapper
                          component={Select}
                          type="multi"
                          key={JSON.stringify(selectedPillFilters)}
                          value={selectedPillFilters}
                          style={{ fontWeight: 'normal' }}
                          onChange={(value: unknown) => {
                            const selected = Array.isArray(value) ? (value as TestCaseStatus[]) : [];
                            setSelectedPillFilters(selected);
                            header.column.setFilterValue(selected.length > 0 ? selected : undefined);
                            table.setPageIndex(0);
                          }}
                        >
                          <ReactEDSWrapper component={MenuItem} value={TestCaseStatus.SUCCESS}>
                            Success
                          </ReactEDSWrapper>
                          <ReactEDSWrapper component={MenuItem} value={TestCaseStatus.FAILURE}>
                            Failed
                          </ReactEDSWrapper>
                          <ReactEDSWrapper component={MenuItem} value={TestCaseStatus.SKIPPED}>
                            Skipped
                          </ReactEDSWrapper>
                          <ReactEDSWrapper component={MenuItem} value={TestCaseStatus.WARNING}>
                            Warning
                          </ReactEDSWrapper>
                          <ReactEDSWrapper component={MenuItem} value={TestCaseStatus.RUNNING}>
                            Running
                          </ReactEDSWrapper>
                        </ReactEDSWrapper>
                      ) : (
                        <input
                          type="text"
                          value={(header.column.getFilterValue() ?? '') as string}
                          onChange={(e) => {
                            header.column.setFilterValue(e.target.value);
                            table.setPageIndex(0);
                          }}
                          placeholder="Filter"
                          className={styles.filterInput}
                        />
                      )}
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={testCaseColumns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                <ReactEDSWrapper component={Loading} size="large" />
              </td>
            </tr>
          ) : testCases.length === 0 ? (
            <tr>
              <td colSpan={testCaseColumns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                No test cases available
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={styles.tableCell}
                    style={{
                      borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      <ReactEDSWrapper
        component={Pagination}
        numPages={Math.ceil(total / pagination.pageSize)}
        selectedPage={table.getState().pagination.pageIndex + 1}
        onPaginationChangePage={(page: number) => {
          table.setPageIndex(page - 1);
        }}
        optionsList={[
          { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
          { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
          { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
          { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
          { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
        ]}
        onPaginationChangeSelect={(size: string) => {
          table.setPageSize(Number(size));
          table.setPageIndex(0);
        }}
        withActions
      />
      <TestCaseLogDialog
        log={logs}
        isLoading={isLoading}
        isError={error}
        testCase={selectedTestCase}
        open={showDialog}
        handleClose={handleCloseDialog}
      />
    </>
  );
};

export default TestCaseTable;
